import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import uniqueId from "lodash.uniqueid";
import isEmpty from "lodash.isempty";

import { FieldArrayRenderProps, useFormikContext } from "formik";

import {
  DraggableProvidedDragHandleProps,
  DropResult,
} from "react-beautiful-dnd";

import {
  HvGlobalActions,
  HvTypography,
  HvGrid,
  HvEmptyState,
  HvDropDownMenu,
  useScrollTo,
  HvTooltip,
  HvButton,
  HvBanner,
} from "@hitachivantara/uikit-react-core";
import {
  HvCellProps,
  HvTableColumnConfig,
} from "@hitachivantara/uikit-react-lab";
import {
  Add,
  Delete,
  Drag,
  Edit,
  Info,
} from "@hitachivantara/uikit-react-icons";

import {
  ActionNames,
  useNotificationTemplate,
  UsersDomain,
  useUsers,
  useWorkflowActions,
  WorkflowActionData,
  WorkflowActionInfo,
} from "lib/api";
import { actionsColumn, textColumn } from "components/common/SimpleTable";
import DragDropList from "components/common/DragDropList";
import { NotificationContext } from "lib/context/NotificationContext";

import IncidentDialog from "../Dialogs/IncidentDialog";
import EmailDialog from "../Dialogs/EmailDialog";
import PushDialog from "../Dialogs/PushDialog";
import SmsDialog from "../Dialogs/SmsDialog";

import {
  actionIsInvalid,
  getActionRecipientsList,
  setActionRecipientsList,
} from "./utils";
import useStyles from "./styles";

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

function findUser(users: UsersDomain[] | undefined, id: string) {
  return users?.find((u) => u.id === id);
}

enum DialogType {
  INCIDENT = "INCIDENT",
  EMAIL = "EMAIL",
  PUSH = "PUSH",
  SMS = "SMS",
}

interface DialogData {
  type: DialogType;
  label: string;
  actionType: WorkflowActionInfo;
  action?: WorkflowActionData;
}

interface ActionsProps extends FieldArrayRenderProps {
  selectedEventTypeId?: string;
}

const Actions: React.FC<ActionsProps> = ({
  push,
  remove,
  replace,
  move,
  selectedEventTypeId,
}) => {
  const { t } = useTranslation("workflowAddEdit");
  const classes = useStyles();

  const [dialog, setDialog] = useState<DialogData>();

  const { setShowBanner, setBannerMessage } = useContext(NotificationContext);

  const workflowActionTypes = useWorkflowActions();
  const { data: templatesEmail } = useNotificationTemplate("email_templates", {
    revalidateOnFocus: false,
  });
  const { data: templatesSms } = useNotificationTemplate("sms_templates", {
    revalidateOnFocus: false,
  });
  const { data: templatesPush } = useNotificationTemplate("push_templates", {
    revalidateOnFocus: false,
  });

  /*   const calledPopulateMasterData = useRef(false); */

  /* useEffect(() => {
    if (
      workflowActionTypes?.data.length === 0 &&
      !calledPopulateMasterData.current
    ) {
      calledPopulateMasterData.current = true;

      (async () => {
        await populateMasterData();
        await revaluateWfAction();
      })();
    }
  }, [revaluateWfAction, workflowActionTypes]); */
  const workflowActionTypesMeta = useMemo((): Array<DialogData> => {
    if (workflowActionTypes == null) {
      return [];
    }

    return workflowActionTypes
      .map((actionType) => {
        switch (actionType.action_name) {
          case ActionNames.CreateIncident:
            return {
              type: DialogType.INCIDENT,
              label: t("actions.emptyState.createOrAddToIncident"),
              actionType,
            };
          case ActionNames.NotificationEmail:
            return {
              type: DialogType.EMAIL,
              label: t("actions.emptyState.emailNotification"),
              actionType,
            };
          case ActionNames.NotificationPush:
            return {
              type: DialogType.PUSH,
              label: t("actions.emptyState.pushNotification"),
              actionType,
            };
          case ActionNames.NotificationSms:
            return {
              type: DialogType.SMS,
              label: t("actions.emptyState.smsNotification"),
              actionType,
            };
          default:
            return null;
        }
      })
      .filter(notEmpty);
  }, [t, workflowActionTypes]);

  const {
    values: { actions, eventTypeId },
    setFieldValue,
  } = useFormikContext<WorkflowFormData>();

  const [actionsTableData, hasInvalidActions] = useMemo(() => {
    let anyInvalid = false;
    let foundCreateIncident = false;
    const augmentedActions = actions.map((action) => {
      foundCreateIncident = foundCreateIncident || action.incident_data != null;
      const [invalid, templateName] = actionIsInvalid(
        action,
        templatesEmail?.data.templates,
        templatesPush?.data.templates,
        templatesSms?.data.templates
      );
      anyInvalid = anyInvalid || invalid;

      return {
        ...action,
        templateName,
        invalid,
      };
    });

    return [augmentedActions, anyInvalid];
  }, [
    actions,
    templatesEmail?.data.templates,
    templatesPush?.data.templates,
    templatesSms?.data.templates,
  ]);

  const eventTypeSelected = !isEmpty(eventTypeId);

  const [, setScrollTo] = useScrollTo(
    0,
    "wis-container",
    false,
    100,
    [
      {
        value: "event-type",
      },
    ],
    undefined
  );

  const openDialog = useCallback(
    (config: DialogData, initialValues?: WorkflowActionData) => {
      setShowBanner(false);
      setDialog({ ...config, action: initialValues });
    },
    [setShowBanner]
  );

  const closeDialog = () => {
    setDialog(undefined);
  };

  /* const onContinueOnNoResultSwitch = useCallback(
    (
      event: Event,
      checked: boolean,
      id: string,
      row: HvRowInstance<WorkflowActionData>
    ) => {
      const action = { ...row.original };
      action.continueOnNoResult = !action.continueOnNoResult;
      replace(
        actions.findIndex((s) => s.name === id),
        action
      );
    },
    [actions, replace]
  );

  const onContinueOnErrorSwitch = useCallback(
    (
      event: Event,
      checked: boolean,
      id: string,
      row: HvRowInstance<WorkflowActionData>
    ) => {
      const action = { ...row.original };
      action.continueOnError = !action.continueOnError;
      replace(
        actions.findIndex((s) => s.name === id),
        action
      );
    },
    [actions, replace]
  );
 */
  const addAction = (data: WorkflowActionData) => {
    setDialog(undefined);
    const [invalid] = actionIsInvalid(
      data,
      templatesEmail?.data.templates,
      templatesPush?.data.templates,
      templatesSms?.data.templates
    );
    const action = { ...data, id: uniqueId(), isInvalid: invalid };
    push(action);
  };

  const editAction = (data: WorkflowActionData) => {
    setDialog(undefined);
    const [invalid] = actionIsInvalid(
      data,
      templatesEmail?.data.templates,
      templatesPush?.data.templates,
      templatesSms?.data.templates
    );
    const action = { ...data, isInvalid: invalid };
    replace(
      actions.findIndex((s) => s.id === data.id),
      action
    );
  };

  const deleteActions = (selectedRowIds: Record<string, boolean>) => {
    const selectedRowOrders = Object.keys(selectedRowIds);
    let numberDeleted = 0;

    actions.forEach((action, index) => {
      if (selectedRowOrders.includes(action.id as string)) {
        // remove updates the indexes inside formik, so we need to update indexes while we remove
        remove(index - numberDeleted);
        numberDeleted += 1;
      }
    });
  };

  const reorderAction = (result: DropResult) => {
    move(result.source.index, result.destination?.index as number);
  };

  const EmptyStateMessage: React.FC = useCallback(() => {
    let content;
    if (!eventTypeSelected) {
      content = (
        <Trans
          t={t}
          i18nKey="actions.emptyState.messageNoEventType"
          components={[
            <HvTypography
              variant="link"
              component="a"
              onClick={() => {
                setScrollTo(null, "event-type", 0, undefined);
              }}
              className={classes.link}
            />,
          ]}
        />
      );
    } else {
      content = workflowActionTypesMeta.map((actionType) => {
        return (
          <HvTypography
            key={actionType.type}
            component="span"
            variant="link"
            onClick={() => {
              openDialog(actionType);
            }}
            className={classes.link}
          >
            {actionType.label}
          </HvTypography>
        );
      });
    }

    return <>{content}</>;
  }, [
    classes.link,
    eventTypeSelected,
    workflowActionTypesMeta,
    setScrollTo,
    t,
    openDialog,
  ]);

  const columns = useMemo(
    (): HvTableColumnConfig<WorkflowActionData>[] => [
      textColumn({
        Header: t("actions.columns.name") as string,
        accessor: "name",
        style: { width: "50%" },
        Cell: ({ value }: HvCellProps<WorkflowActionData, string>) => {
          return t(`actions.${value}`);
        },
      }),
      textColumn({
        Header: t("actions.columns.templateName") as string,
        accessor: "templateName",
        style: { width: "50%" },
      }),
      /*  switchColumn(
        {
          Header: () => {
            return (
              <>
                {t("actions.columns.continueOnNoResult")}
                <HvTooltip
                  title={
                    <HvTypography>
                      {t("actions.columns.continueOnNoResultInfo")}
                    </HvTypography>
                  }
                >
                  <div className={classes.infoIconContainer}>
                    <Info />
                  </div>
                </HvTooltip>
              </>
            );
          },
          accessor: "continueOnNoResult",
          style: {
            width: "25%",
            padding: "8px 0 0 8px",
          },
          Cell: ({ value, row }: HvCellProps<WorkflowActionData, boolean>) => {
            return (
              <SwitchColumnCell
                checked={value}
                value={row.id}
                switchLabel={
                  t(
                    "actions.columns.continueOnNoResultSwitchAriaLabel"
                  ) as string
                }
                falseLabel={t("actions.columns.switchOffLabel") as string}
                trueLabel={t("actions.columns.switchOnLabel") as string}
                switchProps={{
                  onChange: (event, checked, val: string) =>
                    onContinueOnNoResultSwitch(event, checked, val, row),
                  readOnly: false,
                }}
              />
            );
          },
        },
        t("actions.columns.continueOnNoResultSwitchAriaLabel"),
        undefined,
        undefined
      ),
      switchColumn(
        {
          Header: () => {
            return (
              <>
                {t("actions.columns.continueOnError")}
                <HvTooltip
                  title={
                    <HvTypography>
                      {t("actions.columns.continueOnErrorInfo")}
                    </HvTypography>
                  }
                >
                  <div className={classes.infoIconContainer}>
                    <Info />
                  </div>
                </HvTooltip>
              </>
            );
          },
          accessor: "data.continueOnError",
          style: {
            width: "25%",
            padding: "8px 0 0 8px",
          },
          Cell: ({ value, row }: HvCellProps<WorkflowActionData, boolean>) => {
            return (
              <SwitchColumnCell
                checked={value}
                value={row.id}
                switchLabel={
                  t("actions.columns.continueOnErrorSwitchAriaLabel") as string
                }
                falseLabel={t("actions.columns.switchOffLabel") as string}
                trueLabel={t("actions.columns.switchOnLabel") as string}
                switchProps={{
                  onChange: (event, checked, val: string) =>
                    onContinueOnErrorSwitch(event, checked, val, row),
                  readOnly: false,
                }}
              />
            );
          },
        },
        t("actions.columns.continueOnError"),
        undefined,
        undefined
      ), */
      actionsColumn({
        id: "actions",
        Cell: ({
          row,
          dragHandleProps,
        }: HvCellProps<WorkflowActionData> & {
          dragHandleProps: DraggableProvidedDragHandleProps;
        }) => {
          return (
            <div className={classes.actionButtons}>
              <HvTooltip
                title={
                  <HvTypography>
                    {t("actions.columns.actions.edit")}
                  </HvTypography>
                }
              >
                <HvButton
                  icon
                  category="ghost"
                  aria-label={t("actions.columns.actions.drag")}
                  onClick={() => {
                    const actionTypeMeta = workflowActionTypesMeta.find(
                      (a) =>
                        a.actionType.pk_action_id === row.original.fk_action_id
                    );

                    if (actionTypeMeta != null) {
                      openDialog(actionTypeMeta, row.original);
                    }
                  }}
                >
                  <Edit />
                </HvButton>
              </HvTooltip>
              <HvTooltip
                title={
                  <HvTypography>
                    {t("actions.columns.actions.drag")}
                  </HvTypography>
                }
              >
                <HvButton
                  icon
                  category="ghost"
                  aria-label={t("actions.columns.actions.drag")}
                  {...{ component: "div" }}
                  {...dragHandleProps}
                >
                  <Drag />
                </HvButton>
              </HvTooltip>
            </div>
          );
        },
      }),
    ],
    [t, classes.actionButtons, workflowActionTypesMeta, openDialog]
  );

  const errorBannerContainerRef = useRef<HTMLDivElement>(null);
  const firstTimeRef = useRef(true);
  useEffect(() => {
    if (!firstTimeRef.current && hasInvalidActions) {
      errorBannerContainerRef.current?.scrollIntoView(false);
    }

    firstTimeRef.current = false;
  }, [hasInvalidActions]);

  const { data: userData } = useUsers(
    {
      shouldRetryOnError: false,
    },
    {
      pageNr: -1,
      pageSize: -1,
      orderBy: encodeURIComponent("name,surname,email"),
      Order: "asc",
      includeGroups: "true",
      isExcludeServiceAccount: "true",
    }
  );

  const didTheCleaning = useRef(false);
  useEffect(() => {
    if (!didTheCleaning.current) {
      didTheCleaning.current = true;
      const removedUsers = new Set();
      const cleanActions = actions
        .map((action: WorkflowActionData) => {
          const actionTypeMeta = workflowActionTypesMeta.find(
            (meta) => meta.actionType.action_name === action.name
          );

          if (actionTypeMeta == null) {
            return {
              ...action,
              isInvalid: actionIsInvalid(
                action,
                templatesEmail?.data.templates,
                templatesPush?.data.templates,
                templatesSms?.data.templates
              )[0],
            };
          }

          const recipients =
            actionTypeMeta.type === DialogType.INCIDENT
              ? action.incident_data?.inc_watchlist ?? []
              : getActionRecipientsList(action, actionTypeMeta.actionType);
          if (recipients.length === 0) {
            return {
              ...action,
              isInvalid: actionIsInvalid(
                action,
                templatesEmail?.data.templates,
                templatesPush?.data.templates,
                templatesSms?.data.templates
              )[0],
            };
          }

          let localFoundInvalidUsers = false;
          const cleanRecipients = recipients.filter((recipient) => {
            const user =
              recipient.id != null
                ? findUser(userData?.data, recipient.id)
                : recipient;

            if (user != null) {
              return true;
            }

            localFoundInvalidUsers = true;
            removedUsers.add(recipient.id);
            return false;
          });

          let invalidAssignee = false;
          if (actionTypeMeta.type === DialogType.INCIDENT) {
            if (action.incident_data?.inc_assigned_user != null) {
              const user = findUser(
                userData?.data,
                action.incident_data?.inc_assigned_user.id
              );

              if (user == null) {
                invalidAssignee = true;
                localFoundInvalidUsers = true;
                removedUsers.add(action.incident_data.inc_assigned_user.id);
              }
            }
          }

          if (localFoundInvalidUsers) {
            if (
              actionTypeMeta.type !== DialogType.INCIDENT &&
              cleanRecipients.length === 0
            ) {
              return null;
            }

            const newAction = {
              ...action,
              isInvalid: actionIsInvalid(
                action,
                templatesEmail?.data.templates,
                templatesPush?.data.templates,
                templatesSms?.data.templates
              )[0],
              id: `${action.id}-repaired`,
            };

            if (actionTypeMeta.type === DialogType.INCIDENT) {
              if (newAction.incident_data != null) {
                newAction.incident_data.inc_watchlist =
                  cleanRecipients as Array<User>;

                // TODO clean assignee if needed
                if (invalidAssignee) {
                  newAction.incident_data.inc_assigned_user = undefined;
                }
              }
            } else {
              setActionRecipientsList(
                newAction,
                actionTypeMeta?.actionType,
                cleanRecipients
              );
            }

            return newAction;
          }

          return {
            ...action,
            isInvalid: actionIsInvalid(
              action,
              templatesEmail?.data.templates,
              templatesPush?.data.templates,
              templatesSms?.data.templates
            )[0],
          };
        })
        .filter((action) => action != null);

      if (
        removedUsers.size > 0 ||
        cleanActions.find((ca) => ca?.isInvalid) != null
      ) {
        if (removedUsers.size > 0) {
          setFieldValue("actions", cleanActions);
          setBannerMessage(
            t("actions.removedUsers", { count: removedUsers.size })
          );
        } else {
          setBannerMessage(t("actions.someActionInvalidMessage"));
        }
        setShowBanner(true);
      }
    }
  }, [
    actions,
    setFieldValue,
    t,
    userData?.data,
    workflowActionTypesMeta,
    setShowBanner,
    setBannerMessage,
    templatesEmail?.data.templates,
    templatesPush?.data.templates,
    templatesSms?.data.templates,
  ]);

  return (
    <>
      <HvGrid container className={classes.root}>
        <HvGrid sm={12} item>
          <HvGlobalActions title={t("actions.title")} variant="section" />
          <HvTypography variant="normalText">
            {t("actions.description")}
          </HvTypography>
        </HvGrid>
        <HvGrid sm={12} item>
          <HvGrid container justifyContent="flex-end">
            <HvGrid item>
              {workflowActionTypesMeta.length === 1 ? (
                <HvButton
                  category="ghost"
                  aria-label={t("common:buttons.add")}
                  onClick={() => {
                    openDialog(workflowActionTypesMeta[0] as DialogData);
                  }}
                >
                  <Add />
                  {t("common:buttons.add")}
                </HvButton>
              ) : (
                <HvDropDownMenu
                  keepOpened={false}
                  classes={{
                    container: classes.container,
                  }}
                  disabled={!eventTypeSelected}
                  onClick={(e, item) => {
                    openDialog(item.params as DialogData);
                  }}
                  icon={
                    <div className={classes.icon}>
                      <Add />
                      <span>{t("common:buttons.add")}</span>
                    </div>
                  }
                  dataList={workflowActionTypesMeta.map((actionType) => ({
                    id: actionType.type,
                    label: actionType.label,
                    params: actionType,
                  }))}
                />
              )}
            </HvGrid>
          </HvGrid>
        </HvGrid>
        <HvGrid sm={12} item>
          <DragDropList
            columns={columns}
            invalidityAccessor="invalid"
            data={actionsTableData}
            actions={[
              {
                id: "delete",
                label: t("actions.buttons.delete"),
                icon: <Delete />,
              },
            ]}
            actionsCallback={deleteActions}
            emptyState={
              <HvEmptyState
                title={
                  eventTypeSelected
                    ? t("actions.emptyState.title")
                    : t("actions.emptyState.titleNoEventType")
                }
                message={<EmptyStateMessage />}
                icon={<Info />}
              />
            }
            onDragEnd={reorderAction}
          />

          <div ref={errorBannerContainerRef}>
            <HvBanner
              open={hasInvalidActions}
              variant="error"
              showIcon
              label={
                <span>
                  {t("actions.invalid.firstLine")}
                  <br />
                  {t("actions.invalid.secondLine")}
                </span>
              }
              className={classes.errorBanner}
              offset={0}
              bannerContentProps={{
                // remove the close button
                action: null,
              }}
              transitionDuration={0}
            />
          </div>
        </HvGrid>
      </HvGrid>

      <IncidentDialog
        key={
          dialog?.type === DialogType.INCIDENT
            ? "incident_dialog"
            : "incident_dialog_closed"
        }
        isOpen={dialog?.type === DialogType.INCIDENT}
        actionType={dialog?.actionType}
        initialValues={dialog?.action}
        onCancel={closeDialog}
        onApply={dialog?.action == null ? addAction : editAction}
        selectedEventTypeId={selectedEventTypeId}
      />
      <EmailDialog
        // changing the key ensures the dialog state is totally reset when open
        key={dialog?.type === DialogType.EMAIL ? "email_open" : "email_closed"}
        isOpen={dialog?.type === DialogType.EMAIL}
        actionType={dialog?.actionType}
        initialValues={dialog?.action}
        onCancel={closeDialog}
        onApply={dialog?.action == null ? addAction : editAction}
      />
      <PushDialog
        key={dialog?.type === DialogType.PUSH ? "push_open" : "push_closed"}
        isOpen={dialog?.type === DialogType.PUSH}
        actionType={dialog?.actionType}
        initialValues={dialog?.action}
        onCancel={closeDialog}
        onApply={dialog?.action == null ? addAction : editAction}
      />
      <SmsDialog
        key={dialog?.type === DialogType.SMS ? "sms_open" : "sms_closed"}
        isOpen={dialog?.type === DialogType.SMS}
        actionType={dialog?.actionType}
        initialValues={dialog?.action}
        onCancel={closeDialog}
        onApply={dialog?.action == null ? addAction : editAction}
      />
    </>
  );
};

export default Actions;
